import React, { useState, useEffect } from 'react';
import { FaPlane, FaTruck } from "react-icons/fa";

const OrderSummary = ({ pricing, paymentMethod, setPaymentMethod, onSubmit, isSubmitDisabled, codCollectAmount, setCodCollectAmount, shippingPreference, setShippingPreference, selectedProducts, neckLabelPrice, partiallyPaidAmount, setPartiallyPaidAmount, onTotalChange, formData }) => {
    const [codInput, setCodInput] = useState(codCollectAmount);
    const [codError, setCodError] = useState("");
    const [shippingError, setShippingError] = useState("");
    const [partialError, setPartialError] = useState("");

    // Helper function to get product selling price (handles both data structures)
    const getProductSellingPrice = (product) => {
        // Check if product has selectedColorVariant (from modal selection)
        if (product.selectedColorVariant && product.selectedColorVariant.totalPrice) {
            return product.selectedColorVariant.totalPrice;
        }
        // Check if product has colorVariants (new structure)
        if (product.colorVariants && Array.isArray(product.colorVariants) && product.colorVariants.length > 0) {
            return product.colorVariants[0].totalPrice || product.colorVariants[0].baseCost;
        }
        // Fallback to old structure
        return product.sellingPrice || product.totalPrice;
    };

    useEffect(() => {
        setCodInput(codCollectAmount);
    }, [codCollectAmount]);

    // --- SHIPPING CALCULATION ---
    const state = formData?.state;
    const canCalculateShipping = !!state && state.trim() !== "" && shippingPreference;
    const shippingChargeValue = canCalculateShipping
        ? calculateShippingCharge({ selectedProducts, paymentMethod, shippingPreference, state })
        : 0;
    const shippingTax = canCalculateShipping ? shippingChargeValue * 0.18 : 0;
    const totalShipping = shippingChargeValue + shippingTax;

    // Calculate neck label total (only if neck labels are enabled)
    const neckLabelTotal = neckLabelPrice > 0 ? (selectedProducts ? selectedProducts.reduce((sum, p) => sum + (neckLabelPrice * p.quantity), 0) : 0) : 0;
    // Calculate taxes (include neck label cost in tax calculation if enabled)
    const taxableAmount = pricing.subtotal + neckLabelTotal;
    const productTax = taxableAmount * 0.05;
    // Calculate total (what you charge the brand)
    const calculatedTotal = pricing.subtotal + neckLabelTotal + (canCalculateShipping ? totalShipping : 0) + productTax;

    // Add a useEffect to call onTotalChange whenever calculatedTotal changes
    useEffect(() => {
        if (typeof onTotalChange === 'function') {
            onTotalChange(calculatedTotal);
        }
    }, [calculatedTotal]);

    const methods = [
        { label: "Air", icon: <FaPlane className="text-lg mr-2" /> },
        { label: "Surface", icon: <FaTruck className="text-lg mr-2" /> },
    ];

    // Calculate product sellingPrice sum (sum of all products' sellingPrice * quantity)
    const productSellingPriceSum = selectedProducts ? selectedProducts.reduce((sum, p) => {
        const sellingPrice = getProductSellingPrice(p);
        return sum + (parseFloat(sellingPrice) || 0) * p.quantity;
    }, 0) : 0;
    // Amount to collect for Partially Paid
    let amountToCollect = productSellingPriceSum;
    if (paymentMethod === 'Partially Paid') {
        amountToCollect = Math.max(productSellingPriceSum - partiallyPaidAmount, 0);
    } else if (paymentMethod === 'COD') {
        amountToCollect = Math.max(productSellingPriceSum - partiallyPaidAmount, 0);
    }

    // Handle COD input change
    const handleCodInputChange = (e) => {
        const value = e.target.value;
        if (value === "") {
            setCodInput("");
            setCodError("Please enter an amount to collect.");
            setCodCollectAmount(0);
        } else {
            const num = parseFloat(value);
            if (isNaN(num) || num < 1) {
                setCodInput(value);
                setCodError("Amount must be at least 1.");
                setCodCollectAmount(0);
            } else {
                setCodInput(num);
                setCodError("");
                setCodCollectAmount(num);
            }
        }
    };

    // On blur, enforce min value
    const handleCodInputBlur = () => {
        if (codInput === "" || isNaN(codInput) || parseFloat(codInput) < 1) {
            setCodInput(1);
            setCodCollectAmount(1);
            setCodError("");
        }
    };

    // Handle submit: check shippingPreference
    const handleSubmit = () => {
        if (!shippingPreference) {
            setShippingError("Please select a shipping preference.");
            return;
        }
        setShippingError("");
        onSubmit();
    };

    function calculateVolumetricWeight(selectedProducts) {
        const baseDimensions = { L: 24, B: 19, H: 3 }; // Base dimensions for the first 2 items
        const stepIncrease = { L: 4, B: 4, H: 1 }; // Dimensions increase per additional 2 products
        const weightPerTShirt = 0.25; // Since 2 T-shirts make 1 slab of 0.5kg
        const weightPerBabyTee = 0.167; // Since 3 Baby Tees make 1 slab of 0.5kg
        let totalWeight = 0;
        let maxLength = baseDimensions.L;
        let maxBreadth = baseDimensions.B;
        let maxHeight = baseDimensions.H;
        if (!Array.isArray(selectedProducts) || selectedProducts.length === 0) {
            return {
                length: baseDimensions.L,
                breadth: baseDimensions.B,
                height: baseDimensions.H,
                weight: 0.5,
                slabs: 1,
            };
        }
        selectedProducts.forEach((product) => {
            const quantity = product.quantity;
            const name = product.productName?.toLowerCase() || "";
            if (quantity && typeof quantity === "number" && quantity > 0) {
                if (name.includes("t-shirt")) {
                    totalWeight += quantity * weightPerTShirt;
                } else if (name.includes("baby tee")) {
                    totalWeight += quantity * weightPerBabyTee;
                } else {
                    // For other products, assume 1 slab per 2 items, 0.5kg per slab
                    const slabs = Math.ceil(quantity / 2);
                    totalWeight += slabs * 0.5;
                }
            }
        });
        if (totalWeight <= 0) totalWeight = 0.5;
        const totalSlabs = Math.ceil(totalWeight / 0.5);
        maxLength = baseDimensions.L + (totalSlabs - 1) * stepIncrease.L;
        maxBreadth = baseDimensions.B + (totalSlabs - 1) * stepIncrease.B;
        maxHeight = baseDimensions.H + (totalSlabs - 1) * stepIncrease.H;
        return {
            length: maxLength,
            breadth: maxBreadth,
            height: maxHeight,
            weight: parseFloat(totalWeight.toFixed(3)),
            slabs: totalSlabs,
        };
    }

    function calculateShippingCharge({ selectedProducts, paymentMethod, shippingPreference, state }) {
        if (!state || !shippingPreference) return 0;
        const specialStates = [
            "North East", "J & K", "Ladakh", "Andaman & Nicobar", "Himachal Pradesh",
            "Sikkim", "Lakshadweep", "Arunachal Pradesh", "Assam", "Manipur",
            "Meghalaya", "Mizoram", "Nagaland", "Tripura"
        ];
        const { slabs } = calculateVolumetricWeight(selectedProducts);
        let baseRate = 0;
        if (shippingPreference === "Air") baseRate = 75;
        else if (shippingPreference === "Surface") baseRate = 65;
        else baseRate = specialStates.includes(state) ? 85 : 65;
        let shippingCharge = baseRate * slabs;
        const sub_total = selectedProducts.reduce((total, product) => {
            const sellingPrice = getProductSellingPrice(product);
            return total + (sellingPrice * product.quantity);
        }, 0);
        if (paymentMethod === "COD") shippingCharge += (sub_total > 2000) ? (sub_total * 0.025) : 50;
        return shippingCharge;
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md space-y-6">
            <h2 className="text-xl font-semibold text-gray-800 border-b pb-4 create-order-subtitle">Order Summary</h2>

            {/* Pricing Details */}
            <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium text-gray-800">
                        ₹{pricing.subtotal.toFixed(2)}
                    </span>
                </div>
                {neckLabelPrice > 0 && (
                    <div className="flex justify-between">
                        <span className="text-gray-600">Neck Label</span>
                        <span className="font-medium text-gray-800">₹{neckLabelTotal.toFixed(2)}</span>
                    </div>
                )}
                <div className="flex justify-between">
                    <span className="text-gray-600">Shipping{shippingPreference ? ` (${shippingPreference})` : ""}</span>
                    <span className="font-medium text-gray-800">
                        {canCalculateShipping ? `₹${shippingChargeValue.toFixed(2)}` : "₹0.00"}
                    </span>
                </div>
                {!canCalculateShipping && (
                    <div className="text-yellow-600 text-xs mt-2">
                        Please fill shipping details and select a shipping method to see the shipping cost.
                    </div>
                )}
                <div className="flex justify-between">
                    <span className="text-gray-600">Product Tax (5%)</span>
                    <span className="font-medium text-gray-800">₹{productTax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Shipping Tax (18%)</span>
                    <span className="font-medium text-gray-800">
                        {canCalculateShipping ? `₹${shippingTax.toFixed(2)}` : "₹0.00"}
                    </span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-4 border-t mt-2 items-center">
                    <span className="text-gray-900">Total</span>
                    <span className="text-gray-900">
                        ₹{calculatedTotal.toFixed(2)}
                    </span>
                </div>
                {paymentMethod === 'COD' && (
                    <div className="flex justify-between text-sm font-semibold pt-2">
                        <span className="text-gray-700">Cash to Collect</span>
                        <span className="text-gray-700">₹{amountToCollect.toFixed(2)}</span>
                    </div>
                )}
                {paymentMethod === 'Partially Paid' && (
                    <div className="flex justify-between text-sm font-semibold pt-2">
                        <span className="text-gray-700">Cash to Collect</span>
                        <span className="text-gray-700">₹{amountToCollect.toFixed(2)}</span>
                    </div>
                )}
            </div>

            {/* Shipping Method */}
            <div>
                <h3 className="font-semibold text-gray-700 mb-3 create-order-subtitle">Shipping Preference <span className="text-red-500">*</span></h3>
                <div className="flex space-x-4">
                    {methods.map(({ label, icon }) => (
                        <div
                            key={label}
                            onClick={() => setShippingPreference(label)}
                            className={`flex items-center justify-center cursor-pointer px-4 py-2 rounded-full border transition-all duration-300 w-full text-center ${
                                shippingPreference === label
                                    ? "bg-blue-500 text-white border-blue-500"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                            }`}
                        >
                            {icon}
                            <span>{label}</span>
                        </div>
                    ))}
                </div>
                {shippingError && <div className="text-xs text-red-500 mt-1">{shippingError}</div>}
            </div>

            {/* Payment Method */}
            <div>
                <h3 className="font-semibold text-gray-700 mb-3 create-order-subtitle">Payment Method</h3>
                <div className="space-y-3">
                    <label className="flex items-center p-3 border rounded-lg cursor-pointer has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50">
                        <input
                            type="radio"
                            name="payment"
                            value="Prepaid"
                            checked={paymentMethod === 'Prepaid'}
                            onChange={e => setPaymentMethod(e.target.value)}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-700">Prepaid</span>
                    </label>
                    <label className="flex items-center p-3 border rounded-lg cursor-pointer has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50">
                        <input
                            type="radio"
                            name="payment"
                            value="COD"
                            checked={paymentMethod === 'COD'}
                            onChange={e => setPaymentMethod(e.target.value)}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-700">Cash on Delivery (COD)</span>
                    </label>
                    <label className="flex flex-col p-3 border rounded-lg cursor-pointer has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50">
                        <div className="flex items-center">
                            <input
                                type="radio"
                                name="payment"
                                value="Partially Paid"
                                checked={paymentMethod === 'Partially Paid'}
                                onChange={e => setPaymentMethod(e.target.value)}
                                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="ml-3 text-sm font-medium text-gray-700">Partially Paid</span>
                        </div>
                        {paymentMethod === 'Partially Paid' && (
                            <div className="mt-3 ml-7 space-y-1">
                                <label className="text-sm text-gray-600">How much has your customer paid you partially?</label>
                                <input
                                    type="number"
                                    value={partiallyPaidAmount}
                                    min={0}
                                    onChange={e => {
                                        const value = e.target.value;
                                        if (value === "" || isNaN(value) || parseFloat(value) < 0) {
                                            setPartialError("Please enter a valid amount (minimum 0). ");
                                            setPartiallyPaidAmount();
                                        } else {
                                            setPartialError("");
                                            setPartiallyPaidAmount(parseFloat(value));
                                        }
                                    }}
                                    className="w-full max-w-xs px-3 py-2 border rounded-md text-sm text-gray-800"
                                />
                                {partialError && <div className="text-xs text-red-500 mt-1">{partialError}</div>}
                                <div className="text-xs text-gray-500 mt-1">
                                    Enter the amount already paid by the customer. The rest will be collected on delivery.
                                </div>
                            </div>
                        )}
                    </label>
                </div>
            </div>

            {/* Submit Button */}
            <button
                onClick={handleSubmit}
                disabled={isSubmitDisabled || (paymentMethod === 'COD' && (codInput === "" || codError)) || (paymentMethod === 'Partially Paid' && partialError)}
                className="w-full bg-blue-600 text-white font-bold py-3 rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
                Place Order
            </button>
        </div>
    );
};

export default OrderSummary;